
import { useState } from "react";
import { useActivities } from "@/hooks/use-activities";
import { ActivityModal } from "@/components/crm/ActivityModal";
import { ActivityEditModal } from "@/components/crm/ActivityEditModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Clock, User, Building, Search, Filter, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { format, parseISO, addMonths, startOfMonth } from "date-fns";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import type { Activity } from "@/hooks/use-activities";

const activityTypeColors = {
  "Meeting": "bg-blue-100 text-blue-800 border-blue-200",
  "Phone Call": "bg-green-100 text-green-800 border-green-200",
  "Email Communication": "bg-purple-100 text-purple-800 border-purple-200",
  "Proposal Presentation": "bg-orange-100 text-orange-800 border-orange-200",
  "Site Visit": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Follow-up": "bg-gray-100 text-gray-800 border-gray-200",
  "Other": "bg-pink-100 text-pink-800 border-pink-200",
};

export default function CrmActivities() {
  const { data: activities, isLoading, error } = useActivities();
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [currentMonthOffset, setCurrentMonthOffset] = useState(0);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  if (isLoading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          <SidebarInset>
            <div className="p-8">
              <div className="text-slate-600">Loading activities...</div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  if (error) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-background">
          <AppSidebar />
          <SidebarInset>
            <div className="p-8">
              <div className="text-red-600">Error loading activities: {error.message}</div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  const handleActivityClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setEditModalOpen(true);
  };

  // Generate the 3 months to display (current + offset + next 2)
  const baseMonth = addMonths(startOfMonth(new Date()), currentMonthOffset);
  const displayMonths = [
    baseMonth,
    addMonths(baseMonth, 1),
    addMonths(baseMonth, 2)
  ];

  // Filter activities based on search term and type filter
  const filteredActivities = activities?.filter(activity => {
    const matchesSearch = !searchTerm || 
      activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.contact?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.contact?.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = typeFilter === "all" || activity.activity_type === typeFilter;
    
    return matchesSearch && matchesType;
  }) || [];

  // Group activities by month for the 3-month view
  const activitiesByMonth = displayMonths.reduce((acc, month) => {
    const monthKey = format(month, "yyyy-MM");
    acc[monthKey] = filteredActivities.filter(activity => {
      const activityMonth = format(parseISO(activity.activity_date), "yyyy-MM");
      return activityMonth === monthKey;
    });
    return acc;
  }, {} as Record<string, typeof filteredActivities>);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-background">
        <AppSidebar />
        <SidebarInset>
          <FixedPageHeader {...PAGE_HEADERS.crmActivities} actions={<div className="hidden md:block"><ActivityModal /></div>} />
          <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            {/* Mobile Add Activity Button */}
            <div className="md:hidden">
              <ActivityModal />
            </div>

            {/* Summary and Navigation */}
            <div className="flex items-center justify-between">
              <Card className="bg-gradient-to-br from-white to-teal-50 border-teal-200 rounded-xl shadow-sm">
                <CardContent className="p-4 hidden md:block">
                  <div className="flex items-center gap-3">
                    <div className="bg-teal-100 p-2 rounded-lg">
                      <Calendar className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-600">Total Activities</div>
                      <div className="text-2xl font-bold text-slate-800">{activities?.length || 0}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonthOffset(currentMonthOffset - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm font-medium text-slate-600 min-w-[200px] text-center">
                  {format(displayMonths[0], "MMM yyyy")} - {format(displayMonths[2], "MMM yyyy")}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentMonthOffset(currentMonthOffset + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Filters */}
            <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                    <Input
                      placeholder="Search activities..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 border-slate-300 focus:border-teal-500 focus:ring-teal-500"
                    />
                  </div>
                  
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger className="border-slate-300 focus:border-teal-500 focus:ring-teal-500">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="Meeting">Meeting</SelectItem>
                      <SelectItem value="Phone Call">Phone Call</SelectItem>
                      <SelectItem value="Email Communication">Email Communication</SelectItem>
                      <SelectItem value="Proposal Presentation">Proposal Presentation</SelectItem>
                      <SelectItem value="Site Visit">Site Visit</SelectItem>
                      <SelectItem value="Follow-up">Follow-up</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* 3-Month Activities View */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {displayMonths.map(month => {
                const monthKey = format(month, "yyyy-MM");
                const monthActivities = activitiesByMonth[monthKey] || [];
                
                return (
                  <Card key={monthKey} className="bg-white border-slate-200 rounded-xl shadow-sm">
                    <CardHeader className="pb-4">
                      <CardTitle className="text-lg text-slate-800 flex items-center justify-between">
                        <span>{format(month, "MMMM yyyy")}</span>
                        <Badge variant="secondary" className="text-xs">
                          {monthActivities.length}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {monthActivities.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                          <Calendar className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">No activities this month</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {monthActivities.map((activity) => {
                            const activityDate = parseISO(activity.activity_date);
                            const performer = activity.staff || activity.partner;
                            
                            return (
                              <div 
                                key={activity.id} 
                                className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 hover:border-teal-300 transition-all cursor-pointer group relative"
                                onClick={() => handleActivityClick(activity)}
                              >
                                <Badge className={`${activityTypeColors[activity.activity_type]} text-xs font-medium absolute top-3 right-3`}>
                                  {activity.activity_type}
                                </Badge>
                                
                                <h4 className="font-semibold text-sm text-slate-800 group-hover:text-teal-600 transition-colors truncate overflow-hidden mb-2 max-w-[calc(100%-6rem)] text-left">
                                  {activity.title}
                                </h4>
                                
                                <div className="flex flex-col gap-1.5 text-xs text-slate-600">
                                  <div className="flex items-center gap-1.5">
                                    <Clock className="h-3.5 w-3.5 text-slate-400" />
                                    <span className="font-medium">{format(activityDate, "MMM dd, yyyy 'at' HH:mm")}</span>
                                  </div>
                                  
                                  {activity.contact && (
                                    <div className="flex items-center gap-1.5">
                                      <User className="h-3.5 w-3.5 text-slate-400" />
                                      <span className="truncate">
                                        {activity.contact.first_name} {activity.contact.last_name}
                                        {activity.contact.organization?.name && (
                                          <span className="text-slate-500"> at {activity.contact.organization.name}</span>
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  
                                  {performer && (
                                    <div className="flex items-center gap-1.5 text-slate-500">
                                      <User className="h-3.5 w-3.5 text-slate-400" />
                                      <span className="truncate text-xs">By {performer.first_name} {performer.last_name}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Activity Edit Modal */}
            {selectedActivity && (
              <ActivityEditModal
                activity={selectedActivity}
                open={editModalOpen}
                onOpenChange={(open) => {
                  setEditModalOpen(open);
                  if (!open) setSelectedActivity(null);
                }}
              />
            )}
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
